import { useMutation } from "@apollo/client";
import { ChangeEventHandler, DetailedHTMLProps, InputHTMLAttributes } from "react";
import { useIntl } from "react-intl";

import { RichTextareaContext } from "@components/fields/RichTextareaProvider";
import useContextSafely from "@hooks/useContextSafely";
import {
  DirectUploadCreateDocument,
  DirectUploadCreateMutation,
  DirectUploadCreateMutationVariables
} from "@typing/Generated";
import { UploadWithFile } from "@typing/Models";
import { computeChecksumMd5 } from "@utils/fileUtils";

type Props = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "aria-label" | "className" | "type"
>;

const RichTextareaFileInput = ({ onChange, ...inputProps }: Props) => {
  const intl = useIntl();

  const { setUploads, setUploadsInProgress } = useContextSafely(RichTextareaContext);

  const [createDirectUpload] = useMutation<DirectUploadCreateMutation, DirectUploadCreateMutationVariables>(
    DirectUploadCreateDocument
  );

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = event => {
    onChange?.(event);
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadsInProgress(currentUploadsInProgress => [...currentUploadsInProgress, ...files]);
      const promises: Promise<UploadWithFile>[] = [];
      Array.from(files).forEach(file => {
        const promise: Promise<UploadWithFile> = new Promise((resolve, reject) => {
          computeChecksumMd5(file).then(checksum => {
            createDirectUpload({
              variables: {
                byteSize: file.size,
                checksum,
                contentType: file.type,
                filename: file.name
              }
            }).then(response => {
              const directUpload = response.data?.directUploadCreate?.directUpload;
              if (directUpload) {
                resolve({ file, upload: directUpload });
              } else {
                reject("NO BLOB ID");
              }
            });
          });
        });
        promises.push(promise);
      });
      Promise.all(promises).then(result => {
        setUploads(currentUploads => [...currentUploads, ...result]);
      });
    }
  };

  return (
    <input
      accept="*"
      aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.attachFiles" })}
      className="ion-hide"
      multiple
      onChange={handleInputChange}
      type="file"
      {...inputProps}
    />
  );
};

export default RichTextareaFileInput;
